import { useQuery } from '@tanstack/react-query';
import { platformApi } from '../../../api/endpoints/platform';
import { ORGANIZATION_GROUP_NAME, PLATFORM_SCOPE_NAME } from '../../../lib/labelConstants';
import { BrandingEditorForm } from '../../../components/branding/BrandingEditorForm';
import { useAuth } from '../../../hooks/useAuth';
import { useBrandingStore } from '../../../stores/brandingStore';
import { resolvePlatformContainerTenantId } from '../../../lib/brandingTenant';

/**
 * Editor de marca del módulo de plataforma. Wrapper delgado sobre `BrandingEditorForm`:
 *  - Drill-down de un tenant → `tenantId` del PATH.
 *  - Branding de la plataforma misma → `tenantId="platform"`.
 *
 * Recibe `tenantId` por prop e inyecta platformApi.getTenantBranding/updateTenantBranding sobre ÉL.
 *
 * `onSaved` (empujar al brandingStore global para aplicar al instante, sin F5) se pasa SÓLO cuando el
 * tenant que se edita es EL MISMO que el usuario logueado tiene aplicado — i.e. el platform admin
 * (user.tenantId '*' → ficha 'platform') editando `/platform/branding` (tenantId="platform"). En el
 * drill-down de OTRO tenant los valores no son los aplicados, así que NO se empuja (igual que antes).
 *
 * Como el form/preview salen del MISMO `BrandingEditorForm`, la UI es pixel-idéntica a la del tenant.
 */
export function PlatformBrandingEditor({ tenantId }: { tenantId: string }) {
    const { user } = useAuth();
    const applyTenant = useBrandingStore((s) => s.applyTenantBranding);
    // El branding aplicado en la app del usuario es el de SU container (wildcard '*' → 'platform').
    // Si se está editando ese mismo tenant, el guardado debe reflejarse en vivo en la propia UI.
    const editingAppliedBranding = resolvePlatformContainerTenantId(user?.tenantId) === tenantId;
    const isPlatform = tenantId === 'platform';
    const { data: tenant } = useQuery({
        queryKey: ['platform', 'tenants', tenantId, 'detail'],
        queryFn: () => platformApi.getTenant(tenantId),
        enabled: !!tenantId && !isPlatform,
    });
    const previewTitle = isPlatform
        ? PLATFORM_SCOPE_NAME
        : tenant?.displayName || ORGANIZATION_GROUP_NAME;

    return (
        <BrandingEditorForm
            tenantId={tenantId}
            getBranding={platformApi.getTenantBranding}
            updateBranding={platformApi.updateTenantBranding}
            queryKeyPrefix={['platform', 'branding']}
            onSaved={editingAppliedBranding ? applyTenant : undefined}
            sidebarTitle={previewTitle}
        />
    );
}
